'use client';

import { useTransition } from 'react';

import { usePathname } from 'next/navigation';
import Link from '~/(tenant)/_components/tenant-link';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CaptchaField } from '@kit/auth/captcha/client';
import { Button } from '@kit/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@kit/ui/drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { PhoneInput } from '@kit/ui/phone-input';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';
import { useIsMobile } from '@kit/ui/use-mobile';
import { cn } from '@kit/ui/utils';

import authConfig from '~/config/auth.config';
import pathsConfig from '~/config/paths.config';

import { GuestCheckoutRegistrationSchema } from '../_lib/schema/guest-registration.schema';
import { createGuestUserAction } from '../_lib/server/guest-registration-actions';
import { useBookingPresentation } from './booking-presentation';

const hasCaptcha = !!authConfig.captchaTokenSiteKey;

const GuestRegistrationWithCaptchaSchema =
  GuestCheckoutRegistrationSchema.extend({
    captchaToken: hasCaptcha
      ? z.string().min(1)
      : z.string().optional().default(''),
  });

type GuestRegistrationWithCaptchaData = z.infer<
  typeof GuestRegistrationWithCaptchaSchema
>;

interface BookingSnapshot {
  bookingType?: string;
  sport?: string | null;
  selectedDuration?: string;
  date?: Date;
  selectedTime?: string;
  trainerName?: string;
  selectedTemplateId?: string;
  selectedLocationId?: string | null;
  locations?: Array<{ id: string; slug?: string }>;
}

interface GuestRegistrationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (
    userId: string,
    email: string,
    name: string,
    phone: string,
  ) => void;
  bookingState?: BookingSnapshot;
}

function buildBookingReturnUrl(
  pathname: string,
  bookingState?: BookingSnapshot,
) {
  const params = new URLSearchParams();

  // 'all' is the synthetic studio default — re-applied automatically on return,
  // no need to round-trip through the URL.
  if (bookingState?.bookingType && bookingState.bookingType !== 'all') {
    params.set('type', bookingState.bookingType);
  }
  if (bookingState?.sport) {
    params.set('activity', bookingState.sport);
  }
  if (bookingState?.selectedDuration) {
    const numericDuration = bookingState.selectedDuration.replace(/\D/g, '');
    if (numericDuration) {
      params.set('duration', numericDuration);
    }
  }
  if (bookingState?.date) {
    const y = bookingState.date.getFullYear();
    const m = String(bookingState.date.getMonth() + 1).padStart(2, '0');
    const d = String(bookingState.date.getDate()).padStart(2, '0');
    params.set('date', `${y}-${m}-${d}`);
  }
  if (bookingState?.selectedTime) {
    params.set('time', bookingState.selectedTime);
  }
  if (bookingState?.trainerName) {
    params.set('coach', bookingState.trainerName);
  }
  if (bookingState?.selectedTemplateId) {
    params.set('class', bookingState.selectedTemplateId);
  }
  if (bookingState?.selectedLocationId && bookingState.locations) {
    const loc = bookingState.locations.find(
      (l) => l.id === bookingState.selectedLocationId,
    );
    if (loc?.slug) {
      params.set('location', loc.slug);
    }
  }

  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function GuestRegistrationDrawer({
  isOpen,
  onClose,
  onSuccess,
  bookingState,
}: GuestRegistrationDrawerProps) {
  const presentation = useBookingPresentation();
  const [pending, startTransition] = useTransition();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const { t } = useTranslation('auth');

  const form = useForm<GuestRegistrationWithCaptchaData>({
    resolver: zodResolver(GuestRegistrationWithCaptchaSchema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      captchaToken: '',
    },
  });

  // Track captcha loading state for UI (timeout logging handled by useCaptcha hook)
  const captchaToken = form.watch('captchaToken');
  const captchaLoading = hasCaptcha && !captchaToken;

  const onSubmit = (data: GuestRegistrationWithCaptchaData) => {
    startTransition(async () => {
      try {
        const result = await createGuestUserAction(data);
        if (result.success && result.data?.userId) {
          onSuccess(
            result.data.userId,
            result.data.email ?? '',
            data.name,
            data.phoneNumber,
          );
        } else if (!result.success) {
          const errorMessage =
            result.error || t('guestRegistration.toastBookingFailed');
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error('Guest registration error:', error);
        toast.error(t('guestRegistration.toastAccountFailed'));
      }
    });
  };

  const returnUrl = buildBookingReturnUrl(pathname, bookingState);
  const signInUrl = `${pathsConfig.auth.signIn}?next=${encodeURIComponent(returnUrl)}`;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      direction={isMobile ? 'bottom' : 'right'}
    >
      <DrawerContent
        className={cn(isMobile ? 'pb-8' : 'max-h-full', presentation.className)}
        style={{
          ...presentation.style,
          paddingBottom: isMobile
            ? 'calc(env(safe-area-inset-bottom, 0px) + 2rem)'
            : undefined,
          ...(!isMobile && { paddingTop: 'env(safe-area-inset-top, 0px)' }),
        }}
      >
        <DrawerHeader className="text-left">
          <DrawerTitle className="sr-only">
            <Trans
              i18nKey="auth:guestRegistration.title"
              defaults="Continue as a Guest"
            />
          </DrawerTitle>
        </DrawerHeader>

        <div className="max-h-[80vh] overflow-y-auto px-4 pb-6">
          <div className="space-y-5">
            <div className="space-y-2">
              <h2 className="text-xl font-semibold tracking-tight">
                <Trans
                  i18nKey="auth:guestRegistration.title"
                  defaults="Continue as a Guest"
                />
              </h2>
              <p className="text-muted-foreground text-sm">
                <Trans
                  i18nKey="auth:guestRegistration.description"
                  defaults="Create a quick account to complete your court booking."
                />
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Trans
                          i18nKey="auth:guestRegistration.nameLabel"
                          defaults="Full Name"
                        />
                        <span className="ms-1 text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          autoComplete="name"
                          placeholder={t('guestRegistration.namePlaceholder', {
                            defaultValue: 'Enter your full name',
                          })}
                          disabled={pending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Trans
                          i18nKey="auth:guestRegistration.phoneLabel"
                          defaults="Phone Number"
                        />
                        <span className="ms-1 text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <PhoneInput
                          popoverClassName={presentation.className}
                          popoverStyle={presentation.style}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={pending}
                          placeholder={t('guestRegistration.phonePlaceholder', {
                            defaultValue: 'Enter phone number',
                          })}
                          autoComplete="tel"
                          data-test="guest-registration-phone"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Trans
                          i18nKey="auth:guestRegistration.emailLabel"
                          defaults="Email Address"
                        />
                        <span className="ms-1 text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          autoComplete="email"
                          placeholder={t('guestRegistration.emailPlaceholder', {
                            defaultValue: 'Enter your email address',
                          })}
                          disabled={pending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-center">
                  <CaptchaField
                    siteKey={authConfig.captchaTokenSiteKey}
                    control={form.control}
                    name="captchaToken"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={pending}
                    className="flex-1"
                  >
                    <Trans i18nKey="common:cancel" defaults="Cancel" />
                  </Button>

                  <Button
                    type="submit"
                    disabled={pending || captchaLoading}
                    className="flex-1"
                  >
                    {captchaLoading ? (
                      <span className="animate-in fade-in slide-in-from-bottom-24">
                        <Trans i18nKey="auth:verifyingCaptcha" />
                      </span>
                    ) : pending ? (
                      <>
                        <Loader2 className="me-2 h-4 w-4 animate-spin" />
                        <Trans
                          i18nKey="auth:guestRegistration.creating"
                          defaults="Creating..."
                        />
                      </>
                    ) : (
                      <Trans
                        i18nKey="auth:guestRegistration.createAccount"
                        defaults="Book as a Guest"
                      />
                    )}
                  </Button>
                </div>
              </form>
            </Form>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background text-muted-foreground px-2">
                  <Trans i18nKey="auth:or" defaults="or" />
                </span>
              </div>
            </div>

            <Button variant="outline" size="lg" asChild className="w-full">
              <Link href={signInUrl} onClick={onClose}>
                <Trans i18nKey="auth:signIn" defaults="Log In" />
              </Link>
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
